import SwitchField from "components/Forms/SwitchField";
import { RuleType } from "pages/Rules/common/types";
import { useTranslation } from "react-i18next";
import {
  Box,
  SwitchProps,
  TextField,
  TextFieldProps,
  Typography,
} from "@material-ui/core";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";
import { ActionState } from "./types";
import { Field, FormFields } from "pages/Rules/common/FormFields";
import { ErrorMessage } from "./Styled";
import { isBlank } from "utils/string";
import { useRuleContext } from "../../context/ruleContext";

interface Props {
  values: ActionState;
  setValues: (updates: Partial<ActionState>) => void;
}

export default function PasswordNotificationSettings(props: Props) {
  const { values, setValues } = props;
  const { t } = useTranslation("rules");
  const { state } = useRuleContext();
  const isEnabled = Boolean(values?.passwordNotificationText);
  const notification = values.passwordNotificationText;
  const isZipAction = state.rule.actionType === "ZIP_ATTACHMENTS";

  const handleToggleNotification: SwitchProps["onChange"] = (_, checked) => {
    if (!checked) return setValues({ passwordNotificationText: null });

    const senderFields = {
      senderAllLineText: t("passwordNotifications.senderAllLineText.default"),
      senderLineText: t("passwordNotifications.senderLineText.default"),
    };

    setValues({
      passwordNotificationText: {
        recipientLineText: t("passwordNotifications.recipientLineText.default"),
        subjectTag: "[Password Notification]",
        ...(isZipAction && { ...senderFields }),
      },
    });
  };

  const onChange: TextFieldProps["onChange"] = e => {
    const { name, value } = e.target;

    setValues({
      passwordNotificationText: {
        ...values.passwordNotificationText,
        [name]: value,
      },
    });
  };

  return (
    <>
      <SwitchField
        name="passwordNotificationText"
        label={t("passwordNotifications.enable.label")}
        description={t("passwordNotifications.enable.desc")}
        value={isEnabled}
        onChange={handleToggleNotification}
      />
      {isEnabled && (
        <Box mt="1.5rem" ml="1rem">
          <FormFields>
            <Field>
              <TextField
                name="recipientLineText"
                variant="outlined"
                label={t("passwordNotifications.recipientLineText.label")}
                value={notification.recipientLineText}
                onChange={onChange}
                error={isBlank(notification.recipientLineText)}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.MESSAGE }}
              />
              <ErrorMessage
                isError={isBlank(notification.recipientLineText)}
                wording={t("common:cannotBeBlank")}
              />
            </Field>

            {isZipAction && (
              <>
                <Field>
                  <TextField
                    name="senderLineText"
                    variant="outlined"
                    label={t("passwordNotifications.senderLineText.label")}
                    value={notification.senderLineText}
                    onChange={onChange}
                    error={isBlank(notification.senderLineText)}
                    inputProps={{ maxLength: NOTIFICATION_LIMITS.MESSAGE }}
                  />
                  <ErrorMessage
                    isError={isBlank(notification.senderLineText)}
                    wording={t("common:cannotBeBlank")}
                  />
                </Field>
                <Field>
                  <TextField
                    name="senderAllLineText"
                    variant="outlined"
                    label={t("passwordNotifications.senderAllLineText.label")}
                    value={notification.senderAllLineText}
                    onChange={onChange}
                    error={isBlank(notification.senderAllLineText)}
                    inputProps={{ maxLength: NOTIFICATION_LIMITS.MESSAGE }}
                  />
                  <ErrorMessage
                    isError={isBlank(notification.senderAllLineText)}
                    wording={t("common:cannotBeBlank")}
                  />
                </Field>
              </>
            )}

            <Field>
              <TextField
                name="subjectTag"
                variant="outlined"
                label={t("passwordNotifications.subjectTag.label")}
                value={notification.subjectTag}
                onChange={onChange}
                error={isBlank(notification.subjectTag)}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.SUBJECT_TAG }}
              />
              <Typography variant="caption" color="textSecondary">
                {t("passwordNotifications.subjectTag.hint")}
              </Typography>
              <ErrorMessage
                isError={isBlank(notification.subjectTag)}
                wording={t("common:cannotBeBlank")}
              />
            </Field>
          </FormFields>
        </Box>
      )}
    </>
  );
}

export function isAllowPasswordNotification(type: RuleType) {
  return (
    type === "REPLACE_ATTACHMENTS" ||
    type === "ZIP_ATTACHMENTS" ||
    type === "PROTECT_EMAILS"
  );
}
